import request from "@/utils/request";

/**
 * @template T
 * @typedef {Object} ApiResponse
 * @property {number} code
 * @property {string} msg
 * @property {T} data
 */

/**
 * 0 = Open, 2 = Closed
 * @typedef {0 | 2} OrderStatus
 */

/**
 * @typedef {Object} GetOrdersRequest
 * @property {string} fromDate "YYYY-MM-DD HH:mm:ss"
 * @property {string} toDate "YYYY-MM-DD HH:mm:ss"
 * @property {number} pageSize
 * @property {number} pageNum zero-based
 * @property {string} searchKey
 * @property {OrderStatus[]} status
 */

/**
 * Fields shared by getOrders records and getOrder
 * @typedef {Object} OrderSummary
 * @property {string} orderNo
 * @property {number} invoiceId 0 until an invoice is created from the order
 * @property {string} debtorName
 * @property {string} debtorPhone
 * @property {string} debtorEmail
 * @property {string} debtorAddressLine1
 * @property {string} debtorAddressLine2
 * @property {string} debtorCity
 * @property {string} debtorState
 * @property {string} debtorPostcode
 * @property {string} debtorCountry
 * @property {number} debtorTermId
 * @property {string} debtorTermDesc
 * @property {string} orderDate "YYYY-MM-DD HH:mm:ss"
 * @property {string} dispatchDate "YYYY-MM-DD HH:mm:ss"
 * @property {number} amount
 * @property {string} deliveryAddress
 */

/**
 * TOrder entity (getOrders record)
 * @typedef {Object} OrderListFields
 * @property {number} orderID
 * @property {number} debtorId
 * @property {OrderStatus} status
 */

/**
 * @typedef {OrderSummary & OrderListFields} Order
 */

/**
 * MyBatis-Plus IPage
 * @typedef {Object} GetOrdersResponseData
 * @property {Order[]} records
 * @property {number} total
 * @property {number} pages
 * @property {number} current
 * @property {number} size
 */

/**
 * Order line item. Same shape as an invoice item so it can be forwarded to
 * createInvoice unchanged.
 * @typedef {Object} OrderItem
 * @property {number} configId
 * @property {?number} itemId
 * @property {string} itemNotes
 * @property {number} itemType
 * @property {number} locationId
 * @property {number} partId
 * @property {number} partType
 * @property {number} purchaseOrderId
 * @property {number} quantity
 * @property {number} rrp
 * @property {number} stockItemId
 * @property {number} stockPartId
 * @property {string} tagNo
 * @property {number} vehicleId
 * @property {number} unitPrice
 * @property {number} subTotalEx
 * @property {number} subTotalInc
 * @property {number} tax1Total
 * @property {?string} alternateText
 * @property {?string} condition
 * @property {?number} discountTotal
 * @property {?string} engine
 * @property {string} location
 * @property {string} make
 * @property {string} model
 * @property {string} modelType
 * @property {string} part
 * @property {string} partNo
 * @property {number} priceEx
 * @property {?string} priceChangeAuth
 * @property {number} priceInc
 * @property {?string} priceType
 * @property {string} series
 * @property {?string} transmission
 * @property {string} type
 * @property {?string} years
 * @property {number} makeId
 * @property {number} modelId
 * @property {number} seriesId
 * @property {number} modelTypeId
 * @property {?number} engineId
 * @property {?number} transId
 * @property {number} typeId
 * @property {number} stockId
 * @property {?number} colorId
 * @property {number} itemCondition
 */

/**
 * getOrder only fields (note: orderId, not orderID; no status)
 * @typedef {Object} OrderDetailFields
 * @property {number} orderId
 * @property {OrderItem[]} itemsRequestVoList
 */

/**
 * @typedef {OrderSummary & OrderDetailFields & InvoiceTotals} OrderDetail
 */

/**
 * @typedef {Object} InvoiceCustomerInfo
 * @property {number} customerId
 * @property {string} name
 * @property {string} invoiceEmail
 * @property {string} invoicePhone
 * @property {string} billingPhone
 * @property {string} billingAddress
 * @property {string} billingSuburb
 * @property {number} billingPosCode
 * @property {number} billingCountry
 * @property {string} billingState
 * @property {string} deliveryName
 * @property {string} deliveryAddress
 * @property {string} deliverySuburb
 * @property {number} deliveryPostcode
 * @property {string} deliveryState
 * @property {number} deliveryCountry
 * @property {string} xeroContactID
 */

/**
 * @typedef {Object} InvoiceTotals
 * @property {number} itemTotal
 * @property {number} itemTotalExcOtherCharges
 * @property {number} itemTotalOtherCharges
 * @property {number} tax1Total
 * @property {number} totalIncl
 */

/**
 * @typedef {Object} CreateInvoiceOrderInfo
 * @property {OrderItem[]} itemsRequestVoList
 * @property {string} orderNo
 * @property {number} orderId
 * @property {number} salesPerson
 * @property {string} partsTrader
 */

/**
 * @typedef {CreateInvoiceOrderInfo & InvoiceCustomerInfo & InvoiceTotals} CreateInvoiceRequest
 */

/**
 * TODO: confirm response shape (assumed to match saveInvoicesInfo)
 * @typedef {Object} CreateInvoiceResponseData
 * @property {number} invoiceID
 */

/**
 * @param {GetOrdersRequest} data
 * @param {AbortSignal} [signal]
 * @returns {Promise<ApiResponse<GetOrdersResponseData>>}
 */
export function getOrders(data, signal) {
  return request({
    url: "/mgr/order/getOrders",
    method: "post",
    data,
    signal,
  });
}

/**
 * Number of open orders
 * @returns {Promise<ApiResponse<number>>}
 */
export function getOpenOrderCount() {
  return request({
    url: "/mgr/order/count",
    method: "get",
  });
}

/**
 * @param {number | string} id
 * @returns {Promise<ApiResponse<OrderDetail>>}
 */
export function getOrder(id) {
  return request({
    url: `/mgr/order/getOrder/${id}`,
    method: "get",
  });
}

/**
 * @param {CreateInvoiceRequest} data
 * @returns {Promise<ApiResponse<CreateInvoiceResponseData>>}
 */
export function createInvoice(data) {
  return request({
    url: "/mgr/order/createInvoice",
    method: "post",
    data,
  });
}
